import React from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

interface RouteNameMap {
  [path: string]: string;
}

const routeToPageName: RouteNameMap = {
  '/admin': 'Back Office',
  '/dashboard': 'My Personal Dashboard',
  '/': 'Home',
  '/products': 'Products',
  '/companies': 'Companies',
  '/crm': 'CRM Management',
};

interface BackLinkProps {
  to?: string;
  label?: string;
  /** Alias of `label`. */
  text?: string;
  className?: string;
}

function fromPathOf(location: Location): string | undefined {
  const state = location.state as { from?: string } | null;
  return state?.from;
}

function resolveBackDestination(location: Location, to?: string): string {
  if (to) return to;

  const fromPath = fromPathOf(location);
  if (fromPath) return fromPath;

  const pathParts = location.pathname.replace(/\/$/, '').split('/').filter(Boolean);
  if (pathParts.length > 1) return '/' + pathParts.slice(0, -1).join('/');
  if (pathParts[0] === 'admin') return '/admin';
  return '/dashboard';
}

function resolveBackLabel(location: Location, label?: string): string {
  if (label) return label;

  const fromPath = fromPathOf(location);
  if (fromPath) return `Back to ${routeToPageName[fromPath] || fromPath}`;

  if (location.pathname.startsWith('/admin/')) return 'Back to Back Office';
  return 'Back';
}

function BackLink({ to, label, text, className = '' }: BackLinkProps): React.ReactElement {
  const location = useLocation();
  const { isDark } = useTheme();

  return (
    <Link
      to={resolveBackDestination(location, to)}
      className={[
        `tap-target text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
          isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
        }`,
        className
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ArrowLeftIcon className="mr-1 h-4 w-4" aria-hidden="true" />
      <span>{resolveBackLabel(location, label ?? text)}</span>
    </Link>
  );
}

export default BackLink;
